import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import { examQueries } from '@/data/query-keys/examQueryKey';
import {
  createExam,
  deleteExam,
  updateExam,
  updateExamOrder,
} from '@/data/services/examServices';
import { ExamDTO } from '@/models/Exam';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';

import CoursePath from '@/models/CoursePath';

export type BaseExamCoursePath = Pick<CoursePath, 'id' | 'slug'>;

type UseExamsProps = {
  coursePath: BaseExamCoursePath;
};

type UpdateMutationArgs = {
  examId: number;
  data: Partial<ExamDTO>;
};

const useExams = ({ coursePath }: UseExamsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examsTab',
  });
  const coursePathId = coursePath.id;
  const {
    results: exams,
    isInitialLoading,
    cacheManager,
  } = useListService({
    ...examQueries.list({
      coursePathId,
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled: !!coursePathId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: (body: Partial<ExamDTO>) =>
      createExam({ ...body, coursePathId }),
    onSuccess: exam => {
      cacheManager.add(exam);
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ examId, data }: UpdateMutationArgs) =>
      updateExam(examId, data),
    onSuccess: exam => {
      cacheManager.update(exam);
      alert.success(t('updateSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteExam,
    onSuccess: (_, examId) => {
      cacheManager.remove(examId);
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const reorderMutation = useMutation({
    onMutate: (examIds: number[]) => {
      const reordered = reorder(exams, examIds);
      cacheManager.set(reordered);
    },
    mutationFn: (examIds: number[]) =>
      updateExamOrder(
        coursePath.slug,
        examIds.map(id => ({ id })),
      ),
    onSuccess: () => alert.success(t('reorderSuccess')),
    onError: err => {
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  return {
    exams,
    isLoading: isInitialLoading,
    createExam: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateExam: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteExam: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    reorderExams: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
  };
};

export default useExams;
